import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type FactoryDetail,
  type FactoryStageDetail,
  type FactoryStepAccess,
  type FactorySummary,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { NewWorkItem } from '../components/NewWorkItem.tsx';
import { StageStrip } from '../components/StageStrip.tsx';
import { describeTrigger } from '../components/triggers.ts';
import type { Route } from '../routes.ts';

/**
 * The factories: each lifecycle as a row, and one opened to its stages.
 *
 * A stage is a workflow standing in for a step of the lifecycle, and the
 * detail is an *access review* of it: what the stage is told, what it can
 * reach, which tools it may call, under which policy, on which model, for how
 * much. All of it is read from the workflow document on every load, so what
 * the screen says a stage may do is what the run loop will let it do.
 *
 * There is no edit control here, and the reason narrowed. Editing a stage is
 * editing its workflow, and the builder is one click away on every stage;
 * reshaping the lifecycle itself — adding a stage, moving a gate — is a `PUT`
 * to `/api/v1/factory/factories/:id` or the `save_factory` control tool, which
 * is how an agent inside the factory improves the factory. A form for it here
 * is worth building and is not built.
 */
export function FactoriesView({
  factories,
  factoryId,
  go,
}: {
  factories: FactorySummary[];
  factoryId: string | null;
  go(route: Partial<Route> & { tab: Route['tab'] }, replace?: boolean): void;
}) {
  if (factoryId) {
    return <FactoryDetailView factoryId={factoryId} factories={factories} go={go} />;
  }

  const delivery = factories.filter((factory) => factory.kind === 'delivery');
  const support = factories.filter((factory) => factory.kind === 'support');

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Factories</h1>
          <p className="page-lede">
            A factory is a lifecycle: an event that starts it, the stages work moves through, and
            where a person is asked. Each stage runs under its own prompt and its own access.
          </p>
        </div>
        <a
          className="button"
          href="https://github.com/onboardiq/reef/blob/main/docs/factory/README.md"
          target="_blank"
          rel="noreferrer"
        >
          How factories work <Icon name="external" />
        </a>
      </header>

      {factories.length === 0 && (
        /*
         * An empty catalog is a legal state — this deployment has one — and
         * without this the screen was a heading over nothing, which reads as a
         * page that failed to load rather than one with nothing to show yet.
         */
        <section className="panel">
          <p className="panel-quiet">
            No factories yet. A factory is a lifecycle — an entry event and the workflows its
            stages run, in order — written through <code>PUT /api/v1/factory/factories/:id</code>{' '}
            or the <code>save_factory</code> tool on reef's own MCP server. There is no form here
            yet; an agent or a person with workspace admin creates the first one.
          </p>
        </section>
      )}

      <FactoryGroup
        title="Delivery"
        lede="Move a piece of work from a signal to a release."
        factories={delivery}
        go={go}
      />
      <FactoryGroup
        title="Support"
        lede="Run beside the delivery lifecycle: repair, maintain, learn."
        factories={support}
        go={go}
      />
    </div>
  );
}

function FactoryGroup({
  title,
  lede,
  factories,
  go,
}: {
  title: string;
  lede: string;
  factories: FactorySummary[];
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
}) {
  if (factories.length === 0) return null;
  return (
    <section className="page-section">
      <header className="section-head">
        <h2>{title}</h2>
        <span className="hint">{lede}</span>
      </header>
      <div className="factory-cards">
        {factories.map((factory) => (
          <article key={factory.id} className="factory-card">
            <header>
              <div>
                <h3>{factory.name}</h3>
                <p className="hint">{factory.description}</p>
              </div>
              <button
                className="primary"
                onClick={() => go({ tab: 'factories', factoryId: factory.id })}
              >
                Open <Icon name="arrow" />
              </button>
            </header>
            <dl className="facts-row">
              <div>
                <dt>Starts when</dt>
                <dd>{factory.entry.description}</dd>
              </div>
              <div>
                <dt>Stages</dt>
                <dd>
                  {factory.deployed} of {factory.stages.length} deployed
                </dd>
              </div>
              {factory.kind === 'delivery' && (
                <div>
                  <dt>In flight</dt>
                  <dd>{factory.itemsInFlight} item{factory.itemsInFlight === 1 ? '' : 's'}</dd>
                </div>
              )}
              <div>
                <dt>Waiting on a person</dt>
                <dd>{factory.stages.reduce((n, stage) => n + stage.waiting, 0)}</dd>
              </div>
            </dl>
            <StageStrip
              stages={factory.stages}
              closesLoop={factory.closesLoop}
              onSelect={() => go({ tab: 'factories', factoryId: factory.id })}
            />
          </article>
        ))}
      </div>
    </section>
  );
}

function FactoryDetailView({
  factoryId,
  factories,
  go,
}: {
  factoryId: string;
  factories: FactorySummary[];
  go(route: Partial<Route> & { tab: Route['tab'] }, replace?: boolean): void;
}) {
  const [factory, setFactory] = useState<FactoryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stageId, setStageId] = useState<string | null>(null);
  const [filing, setFiling] = useState(false);
  const [started, setStarted] = useState<{ runId: string; workflowId: string } | null>(null);

  useEffect(() => {
    let current = true;
    setFactory(null);
    setError(null);
    setStageId(null);
    api
      .factory(factoryId)
      .then((result) => current && setFactory(result.factory))
      .catch((caught) => current && setError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [factoryId]);

  /*
   * Re-read after a stage is started, in place. Clearing the factory first
   * would unmount the stage panel — and with it the "started as run" notice
   * the person is reading — to show "Loading" for a round trip; a failed
   * re-read keeps the last good screen for the same reason.
   */
  const reload = () => {
    api
      .factory(factoryId)
      .then((result) => setFactory(result.factory))
      .catch(() => undefined);
  };

  const stage = useMemo(() => {
    if (!factory) return null;
    return factory.stages.find((entry) => entry.id === stageId) ?? factory.stages[0] ?? null;
  }, [factory, stageId]);

  if (error) {
    return (
      <div className="page">
        <div className="banner danger">{error}</div>
        <button className="ghost" onClick={() => go({ tab: 'factories' })}>
          <Icon name="arrow" /> Back to factories
        </button>
      </div>
    );
  }

  if (!factory) {
    return (
      <div className="page">
        <p className="hint">Loading the factory…</p>
      </div>
    );
  }

  return (
    <div className="page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <button className="linkish" onClick={() => go({ tab: 'factories' })}>
          Factories
        </button>
        <span className="crumb-sep">/</span>
        <span>{factory.name}</span>
      </nav>

      <header className="page-head">
        <div>
          <h1>{factory.name}</h1>
          <p className="page-lede">{factory.description}</p>
        </div>
        <div className="row">
          {factory.kind === 'delivery' && (
            <button className="primary" onClick={() => setFiling((open) => !open)}>
              <Icon name="plus" /> New work item
            </button>
          )}
          <button className="ghost" onClick={() => go({ tab: 'work' })}>
            Board <Icon name="arrow" />
          </button>
        </div>
      </header>

      {filing && (
        <section className="panel">
          <header className="panel-head">
            <h2>Describe the work</h2>
          </header>
          <NewWorkItem
            factories={factories}
            initialFactoryId={factory.id}
            onStarted={(runId, workflowId) => {
              setStarted({ runId, workflowId });
              setFiling(false);
            }}
            onCancel={() => setFiling(false)}
          />
        </section>
      )}

      {started && (
        <div className="banner">
          <p style={{ margin: 0 }}>
            Started as run <code>{started.runId}</code>.{' '}
            <button
              className="linkish"
              onClick={() => go({ tab: 'runs', workflowId: started.workflowId, runId: started.runId })}
            >
              Watch it
            </button>
          </p>
        </div>
      )}

      <dl className="facts-row">
        <div>
          <dt>Starts when</dt>
          <dd>{factory.entry.description}</dd>
        </div>
        <div>
          <dt>Signal source</dt>
          <dd>
            <code>{factory.entry.source}</code>
            {factory.entry.connectionId && (
              <>
                {' '}
                via{' '}
                <button
                  className="linkish"
                  onClick={() => go({ tab: 'connections', connectionId: factory.entry.connectionId! })}
                >
                  {factory.entry.connectionId}
                </button>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Stages</dt>
          <dd>
            {factory.deployed} of {factory.stages.length} deployed
          </dd>
        </div>
        {factory.kind === 'delivery' && (
          <div>
            <dt>In flight</dt>
            <dd>{factory.itemsInFlight}</dd>
          </div>
        )}
      </dl>

      <StageStrip
        stages={factory.stages}
        selected={stage?.id ?? null}
        closesLoop={factory.closesLoop}
        onSelect={setStageId}
      />

      {stage && (
        <StagePanel
          stage={stage}
          index={factory.stages.indexOf(stage)}
          factoryId={factory.id}
          factories={factories}
          go={go}
          onStarted={reload}
        />
      )}
    </div>
  );
}

/**
 * Why this person cannot start the stage from here, or null when they can.
 *
 * The same verdict `canTriggerWorkflow` reaches on the Worker, decided from
 * the facts the response already carries — the triggers and the caller's
 * level — so the button is disabled with the reason instead of being offered
 * and refused. `edit` may start anything; `read` may start only a stage whose
 * manual trigger is open to everyone, which is what makes triage a self-serve
 * button and keeps codegen one for editors.
 */
export function startRefusal(stage: FactoryStageDetail): string | null {
  if (!stage.present) return `${stage.workflowId} is not deployed here.`;
  if (!stage.workflow) return 'This stage’s workflow is not shared with you.';
  const manual = stage.workflow.triggers.filter((trigger) => trigger.type === 'manual');
  if (manual.length === 0) {
    return 'This stage has no manual trigger — it is started by another stage.';
  }
  if (stage.workflow.yourLevel === 'read' && !manual.some((trigger) => trigger.triggerFromEveryone)) {
    return 'Starting this stage needs edit access; you have read.';
  }
  return null;
}

function StagePanel({
  stage,
  index,
  factoryId,
  factories,
  go,
  onStarted,
}: {
  stage: FactoryStageDetail;
  index: number;
  factoryId: string;
  factories: FactorySummary[];
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
  onStarted(): void;
}) {
  const [openStep, setOpenStep] = useState<string | null>(null);
  const [filing, setFiling] = useState(false);
  const [started, setStarted] = useState<{ runId: string; workflowId: string } | null>(null);

  useEffect(() => setOpenStep(stage.steps[0]?.id ?? null), [stage]);
  // A form half-filled for Implement must not survive a click on Validate: it
  // would file at the stage the person left, under a heading naming the one
  // they arrived at. Keyed on the id, not the object, so a reload after a
  // start keeps the notice that the start happened.
  useEffect(() => {
    setFiling(false);
    setStarted(null);
  }, [stage.id]);

  const refusal = startRefusal(stage);

  return (
    <section className="stage-panel">
      <header className="stage-panel-head">
        <div>
          <span className="eyebrow">Stage {index + 1}</span>
          <h2>{stage.name}</h2>
          <p className="hint">{stage.description}</p>
        </div>
        <div className="row">
          <button
            className="primary"
            disabled={refusal !== null}
            title={refusal ?? `Start ${stage.workflowId} with a work item`}
            onClick={() => setFiling((open) => !open)}
          >
            <Icon name="play" /> Start this stage
          </button>
          {stage.workflow && (
            <>
              <button
                className="ghost"
                onClick={() => go({ tab: 'runs', workflowId: stage.workflowId })}
              >
                Runs
              </button>
              <button onClick={() => go({ tab: 'build', workflowId: stage.workflowId })}>
                {stage.workflow.yourLevel === 'read' ? 'Read the workflow' : 'Edit in builder'}{' '}
                <Icon name="arrow" />
              </button>
            </>
          )}
        </div>
      </header>

      {filing && refusal === null && (
        <section className="panel">
          <header className="panel-head">
            <h2>Describe the work</h2>
            <span className="hint">Filed at this stage, not at the factory’s front door.</span>
          </header>
          <NewWorkItem
            factories={factories}
            initialFactoryId={factoryId}
            workflowId={stage.workflowId}
            onStarted={(runId, workflowId) => {
              setStarted({ runId, workflowId });
              setFiling(false);
              onStarted();
            }}
            onCancel={() => setFiling(false)}
          />
        </section>
      )}

      {started && (
        <div className="banner">
          <p style={{ margin: 0 }}>
            Started as run <code>{started.runId}</code>.{' '}
            <button
              className="linkish"
              onClick={() => go({ tab: 'runs', workflowId: started.workflowId, runId: started.runId })}
            >
              Watch it
            </button>
          </p>
        </div>
      )}

      <dl className="facts-grid">
        <div>
          <dt>Enters on</dt>
          <dd>{stage.entersOn}</dd>
        </div>
        <div>
          <dt>Produces</dt>
          <dd>{stage.produces}</dd>
        </div>
        <div>
          <dt>Ticket reads</dt>
          <dd>{stage.trackerState ? <span className="pill">{stage.trackerState}</span> : <span className="hint">not mirrored</span>}</dd>
        </div>
        <div>
          <dt>Human gate</dt>
          <dd>
            {stage.gate ? (
              <span className="pill warn">
                a person decides at <code>{stage.gate}</code>
              </span>
            ) : (
              <span className="hint">none — runs unattended</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Workflow</dt>
          <dd>
            <code>{stage.workflowId}</code>{' '}
            {!stage.present && <span className="pill danger">not deployed</span>}
            {stage.present && !stage.visible && <span className="pill">not shared with you</span>}
          </dd>
        </div>
        <div>
          <dt>Right now</dt>
          <dd>
            {stage.running} running · {stage.waiting} waiting on a person · {stage.items} item
            {stage.items === 1 ? '' : 's'}
          </dd>
        </div>
      </dl>

      {stage.workflow && (
        <div className="access-grid">
          <section className="panel">
            <header className="panel-head">
              <h2>How it starts</h2>
            </header>
            <ul className="trigger-list">
              {stage.workflow.triggers.map((trigger, position) => (
                <li key={position}>
                  <span className="pill accent">{trigger.type}</span>
                  <span>{describeTrigger(trigger)}</span>
                </li>
              ))}
              {stage.workflow.triggers.length === 0 && <li className="hint">Started only by another stage.</li>}
            </ul>
          </section>

          <section className="panel">
            <header className="panel-head">
              <h2>What the whole stage may reach</h2>
            </header>
            <div className="chips">
              {stage.workflow.connections.map((connection) => (
                <ConnectionChip key={connection.as} connection={connection} go={go} />
              ))}
              {stage.workflow.connections.length === 0 && (
                <span className="hint">No connections at the workflow level — each step mounts its own.</span>
              )}
            </div>
            <dl className="facts-row tight">
              <div>
                <dt>Policy</dt>
                <dd>
                  {stage.workflow.policy ? (
                    <button className="linkish" onClick={() => go({ tab: 'settings', section: 'policies' })}>
                      {stage.workflow.policy}
                    </button>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt>Budget</dt>
                <dd>{limits(stage.workflow.limits)}</dd>
              </div>
              <div>
                <dt>Your access</dt>
                <dd>{stage.workflow.yourLevel}</dd>
              </div>
            </dl>
          </section>
        </div>
      )}

      {stage.steps.length > 0 && (
        <section className="panel">
          <header className="panel-head">
            <h2>Steps</h2>
            <span className="hint">
              Each step runs its own prompt with its own access. Open one to read exactly what it is
              told and what it can touch.
            </span>
          </header>
          <ol className="steps">
            {stage.steps.map((step, position) => (
              <StepRow
                key={step.id}
                step={step}
                index={position + 1}
                open={openStep === step.id}
                onToggle={() => setOpenStep(openStep === step.id ? null : step.id)}
                go={go}
              />
            ))}
          </ol>
        </section>
      )}

      {/*
        Drawn with nothing in it rather than left out. An idle stage that
        showed no runs section at all gave no sign that it could be run; the
        empty state says how a run gets here, by hand or by the event.
      */}
      <section className="panel">
        <header className="panel-head">
          <h2>Recent runs</h2>
        </header>
        {stage.recentRuns.length === 0 && (
          <p className="stage-runs-empty hint">
            {stage.workflow
              ? <>No runs yet — start one above, or wait for <em>{stage.entersOn}</em>.</>
              : stage.present
                ? 'Runs of this stage are on a workflow not shared with you.'
                : 'Nothing runs here until the workflow is deployed.'}
          </p>
        )}
        {stage.recentRuns.length > 0 && (
          <ul className="run-list">
            {stage.recentRuns.map((run) => (
              <li key={run.id}>
                <button
                  type="button"
                  className="run-line"
                  onClick={() => go({ tab: 'runs', workflowId: stage.workflowId, runId: run.id })}
                >
                  <span className={`run-dot ${runTone(run.status)}`} />
                  <span className="run-line-name mono">{run.id}</span>
                  <span className="hint">{run.status.replace(/_/g, ' ')}</span>
                  <span className="hint">{run.triggerType}</span>
                  <span className="hint">{new Date(run.createdAt).toLocaleString()}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function StepRow({
  step,
  index,
  open,
  onToggle,
  go,
  depth = 0,
}: {
  step: FactoryStepAccess;
  index: number | string;
  open: boolean;
  onToggle(): void;
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
  depth?: number;
}) {
  const [openChild, setOpenChild] = useState<string | null>(null);
  return (
    <li className={`step ${depth > 0 ? 'nested' : ''}`}>
      <button type="button" className="step-head" aria-expanded={open} onClick={onToggle}>
        <span className="step-index">{index}</span>
        <span className="step-title">
          <strong>{step.name}</strong>
          <code>{step.id}</code>
        </span>
        <span className="step-badges">
          {step.requiresApproval && <span className="pill warn">approval</span>}
          {step.fanOut && <span className="pill accent">fan-out</span>}
          {step.watch && <span className="pill accent">watch · {step.watch}</span>}
          <span className="pill">{step.runtime}</span>
          <span className="pill">{step.model.replace(/^anthropic\//, '')}</span>
        </span>
        <Icon name="chevron" className={`icon chevron ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <div className="step-detail">
          <div className="step-prompt">
            <span className="eyebrow">Prompt</span>
            <pre className="prompt">{step.prompt || '(no prompt — inherits the skill alone)'}</pre>
          </div>
          <dl className="step-access">
            <div>
              <dt>Connections</dt>
              <dd className="chips">
                {step.connections.map((connection) => (
                  <ConnectionChip key={connection.as} connection={connection} go={go} />
                ))}
                {step.connections.length === 0 && <span className="hint">none — cannot reach anything</span>}
              </dd>
            </div>
            <div>
              <dt>Tools allowed</dt>
              <dd className="chips">
                {step.toolsAllowed.map((tool) => (
                  <code key={tool} className="tool">
                    {tool}
                  </code>
                ))}
                {step.toolsAllowed.length === 0 && (
                  <span className="hint">everything those connections expose</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Skills</dt>
              <dd className="chips">
                {step.skills.map((skill) => (
                  <button key={skill} className="linkish" onClick={() => go({ tab: 'agents', section: 'skills' })}>
                    {skill}
                  </button>
                ))}
                {step.skills.length === 0 && <span className="hint">none</span>}
              </dd>
            </div>
            <div>
              <dt>Policy</dt>
              <dd>{step.policy ?? '—'}</dd>
            </div>
            <div>
              <dt>Model</dt>
              <dd>
                <code>{step.model}</code>
              </dd>
            </div>
            <div>
              <dt>Limits</dt>
              <dd>{limits(step.limits)}</dd>
            </div>
            {step.requiresApproval && (
              <div>
                <dt>Approval</dt>
                <dd>
                  stops for a person{step.approvalChannel ? ` in ${step.approvalChannel}` : ''}
                </dd>
              </div>
            )}
          </dl>
          {step.childSteps.length > 0 && (
            <ol className="steps">
              {step.childSteps.map((child, position) => (
                <StepRow
                  key={child.id}
                  step={child}
                  index={`${index}.${position + 1}`}
                  open={openChild === child.id}
                  onToggle={() => setOpenChild(openChild === child.id ? null : child.id)}
                  go={go}
                  depth={depth + 1}
                />
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}

function ConnectionChip({
  connection,
  go,
}: {
  connection: { id: string; as: string; actor: string };
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
}) {
  return (
    <button
      type="button"
      className={`identity identity--${connection.actor === 'service' ? 'bot' : connection.actor === 'user' ? 'person' : 'auto'}`}
      title={`${connection.id} mounted as ${connection.as}, acting as ${connection.actor}`}
      onClick={() => go({ tab: 'connections', connectionId: connection.id })}
    >
      {connection.as}
      <span className="hint">{connection.actor}</span>
    </button>
  );
}

function limits(value: { maxRuntimeMs: number | null; maxSpendCents: number | null }): string {
  const parts: string[] = [];
  if (value.maxRuntimeMs != null) parts.push(`${Math.round(value.maxRuntimeMs / 60000)} min`);
  if (value.maxSpendCents != null) parts.push(`$${(value.maxSpendCents / 100).toFixed(2)}`);
  return parts.length ? parts.join(' · ') : 'deployment defaults';
}

function runTone(status: string): string {
  if (status === 'completed') return 'ok';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'awaiting_approval' || status.startsWith('paused')) return 'warn';
  return 'accent';
}
